import { MAX_ALBUM } from "./album";
import type { ChatMessage } from "./client";
import type {
  AlbumMark,
  Content,
  ImportedMessage,
  MediaRef,
  QueravelFile,
  QueravelMark,
  WorkflowMark,
} from "./content";

// Every decrypted message is checked here before it reaches the app. Anyone in a chat can send any
// bytes, including an older or a broken app, so a field of the wrong type must never crash a
// screen. An optional field that is wrong is dropped. A message without what it needs to show
// returns null, and the app shows a short notice in its place.

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const isStr = (v: unknown): v is string => typeof v === "string";
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const isBool = (v: unknown): v is boolean => typeof v === "boolean";

/** The optional field, when it has the right type. */
function opt<T>(v: unknown, ok: (v: unknown) => v is T): T | undefined {
  return ok(v) ? v : undefined;
}

/** Copies the keys whose value is not undefined, so dropped fields leave no trace. */
function defined<T extends Obj>(o: T): T {
  const out: Obj = {};
  for (const [k, v] of Object.entries(o)) if (v !== undefined) out[k] = v;
  return out as T;
}

/** Files a Queravel answer made. Only named ones with an https address are kept. */
function keptArtifacts(v: unknown): QueravelFile[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out: QueravelFile[] = [];
  for (const a of v) {
    if (!isObj(a) || !isStr(a.name) || !a.name || !isStr(a.url) || !/^https:\/\//i.test(a.url)) continue;
    out.push(
      defined<QueravelFile>({
        name: a.name,
        url: a.url,
        size: isNum(a.size) && a.size >= 0 ? a.size : undefined,
        contentType: opt(a.contentType, isStr),
      }),
    );
  }
  return out.length ? out : undefined;
}

/** The largest embedded preview image accepted. Real thumbnails are a few kilobytes. */
const MAX_IMAGE_URL = 512 * 1024;
const IMAGE_URL = /^data:image\/(?:png|jpeg|webp|gif);base64,[A-Za-z0-9+/=]*$/;

/**
 * An image address from a message, when it is an embedded image. Any other address, such as a web
 * link, would let the sender learn when and where the message was opened, so it is ignored.
 */
export function safeImageUrl(v: unknown): string | undefined {
  if (!isStr(v) || v.length > MAX_IMAGE_URL) return undefined;
  return IMAGE_URL.test(v) ? v : undefined;
}

const MEDIA_KINDS = ["photo", "video", "file", "voice"] as const;
type MediaKind = (typeof MEDIA_KINDS)[number];
const isMediaKind = (v: unknown): v is MediaKind => MEDIA_KINDS.includes(v as MediaKind);

function size(v: unknown): number | undefined {
  return isNum(v) && v >= 0 ? v : undefined;
}

/** A whole number of at least 1. */
function positiveInt(v: unknown): number | undefined {
  return isNum(v) && Number.isInteger(v) && v >= 1 ? v : undefined;
}

/**
 * The album a photo or video belongs to. A mark that does not add up is dropped, and the photo
 * then shows on its own.
 */
function albumMark(v: unknown, kind: MediaKind): AlbumMark | undefined {
  if (!isObj(v) || (kind !== "photo" && kind !== "video")) return undefined;
  const { id, index, count } = v;
  if (!isStr(id) || !id || id.length > 64) return undefined;
  if (!isNum(index) || !isNum(count) || !Number.isInteger(index) || !Number.isInteger(count))
    return undefined;
  if (count < 2 || count > MAX_ALBUM || index < 0 || index >= count) return undefined;
  return { id, index, count };
}

/** A file reference. Null when the file could not be found or decrypted with what it holds. */
export function parseMediaRef(v: unknown): MediaRef | null {
  if (!isObj(v)) return null;
  const { key, file_key, iv, sha256 } = v;
  if (!isStr(key) || !key || !isStr(file_key) || !isStr(iv) || !isStr(sha256)) return null;
  const waveform = Array.isArray(v.waveform) ? v.waveform.filter(isNum) : undefined;
  return defined<MediaRef>({
    key,
    file_key,
    iv,
    sha256,
    mime: isStr(v.mime) && v.mime ? v.mime : "application/octet-stream",
    size: size(v.size) ?? 0,
    name: opt(v.name, isStr),
    width: size(v.width),
    height: size(v.height),
    duration: size(v.duration),
    waveform: waveform?.length ? waveform : undefined,
    thumbnail: safeImageUrl(v.thumbnail),
    placeholder: safeImageUrl(v.placeholder),
    // Kept even when it is newer than this app knows, so the app can say to update.
    version: positiveInt(v.version),
    chunk_size: positiveInt(v.chunk_size),
  });
}

function kindFromMime(mime: string): MediaKind {
  if (mime.startsWith("image/")) return "photo";
  if (mime.startsWith("video/")) return "video";
  return "file";
}

function strings(v: unknown): string[] | undefined {
  return Array.isArray(v) ? v.filter(isStr) : undefined;
}

function queravelMark(v: unknown): QueravelMark | undefined {
  if (!isObj(v) || !isStr(v.chat_id)) return undefined;
  const source =
    isObj(v.source) && isStr(v.source.chat) && isStr(v.source.name)
      ? { chat: v.source.chat, name: v.source.name }
      : undefined;
  return defined<QueravelMark>({ chat_id: v.chat_id, label: opt(v.label, isStr), source });
}

function workflowMark(v: unknown): WorkflowMark | undefined {
  if (!isObj(v) || !isStr(v.id) || !isStr(v.name)) return undefined;
  if (v.kind !== "welcome" && v.kind !== "recurring") return undefined;
  return { id: v.id, kind: v.kind, name: v.name };
}

function importedMessage(v: unknown): ImportedMessage | null {
  if (!isObj(v) || !isStr(v.id) || !isStr(v.original_time)) return null;
  const sender = v.original_sender;
  if (sender !== null && !isStr(sender)) return null;
  const media = v.media === undefined ? undefined : (parseMediaRef(v.media) ?? undefined);
  return defined<ImportedMessage>({
    id: v.id,
    original_sender: sender,
    sender_id: opt(v.sender_id, isStr),
    original_time: v.original_time,
    body: opt(v.body, isStr),
    media,
    media_kind: media ? opt(v.media_kind, isMediaKind) : undefined,
    omitted: opt(v.omitted, isStr),
    deleted: opt(v.deleted, isBool),
    edited: opt(v.edited, isBool),
    system: opt(v.system, isBool),
  });
}

/**
 * Checks decrypted message content. Returns it repaired, without any optional field that had the
 * wrong type, or null when it cannot be shown at all.
 */
export function parseContent(v: unknown): Content | null {
  if (!isObj(v) || !isStr(v.type)) return null;
  switch (v.type) {
    case "text":
      if (!isStr(v.body)) return null;
      return defined<Content>({
        type: "text",
        body: v.body,
        // Only formats this version reads are kept. Anything else shows as plain text.
        format: v.format === "md" ? "md" : undefined,
        reply_to: opt(v.reply_to, isStr),
        mentions: strings(v.mentions),
        queravel: queravelMark(v.queravel),
        workflow: workflowMark(v.workflow),
        forwarded: opt(v.forwarded, isBool),
      });
    case "queravel":
      if (!isStr(v.body) || !isStr(v.chat_id)) return null;
      return defined<Content>({
        type: "queravel",
        body: v.body,
        chat_id: v.chat_id,
        references: Array.isArray(v.references) ? v.references : undefined,
        artifacts: keptArtifacts(v.artifacts),
        tools: strings(v.tools),
      });
    case "queravel_title":
      if (!isStr(v.conversation) || !isStr(v.title)) return null;
      return { type: "queravel_title", conversation: v.conversation, title: v.title };
    case "notice":
      return v.event === "queravel_asked" ? { type: "notice", event: "queravel_asked" } : null;
    case "media": {
      const media = parseMediaRef(v.media);
      if (!media) return null;
      const kind = isMediaKind(v.kind) ? v.kind : kindFromMime(media.mime);
      return defined<Content>({
        type: "media",
        media,
        kind,
        caption: opt(v.caption, isStr),
        reply_to: opt(v.reply_to, isStr),
        forwarded: opt(v.forwarded, isBool),
        album: albumMark(v.album, kind),
        mentions: strings(v.mentions),
      });
    }
    case "imported": {
      if (v.source !== "whatsapp" || !isStr(v.import_id) || !Array.isArray(v.messages)) return null;
      const messages = v.messages.map(importedMessage).filter((m): m is ImportedMessage => m !== null);
      return { type: "imported", source: "whatsapp", import_id: v.import_id, messages };
    }
    case "location":
      if (!isNum(v.lat) || !isNum(v.lng) || Math.abs(v.lat) > 90 || Math.abs(v.lng) > 180) return null;
      return defined<Content>({
        type: "location",
        lat: v.lat,
        lng: v.lng,
        title: opt(v.title, isStr),
        live_until: opt(v.live_until, isStr),
      });
    case "contact":
      if (!isStr(v.user_id) || !isStr(v.name)) return null;
      return { type: "contact", user_id: v.user_id, name: v.name };
    case "poll": {
      const options = strings(v.options);
      if (!isStr(v.question) || !options?.length) return null;
      return defined<Content>({
        type: "poll",
        question: v.question,
        options,
        multiple: opt(v.multiple, isBool),
      });
    }
    case "vote":
      if (!isStr(v.poll_id) || !Array.isArray(v.options)) return null;
      return {
        type: "vote",
        poll_id: v.poll_id,
        options: v.options.filter((n): n is number => Number.isInteger(n) && (n as number) >= 0),
      };
    case "reaction":
      if (!isStr(v.key) || !v.key || v.key.length > 64) return null;
      return defined<Content>({ type: "reaction", key: v.key, remove: opt(v.remove, isBool) });
    case "edit":
      if (!isStr(v.body)) return null;
      return defined<Content>({
        type: "edit",
        body: v.body,
        ...(v.format === "md" ? { format: "md" as const } : {}),
        mentions: strings(v.mentions),
      });
    case "call":
      if (!isStr(v.call_id)) return null;
      if (v.media !== "voice" && v.media !== "video") return null;
      if (v.event !== "started" && v.event !== "ended" && v.event !== "missed") return null;
      return defined<Content>({
        type: "call",
        call_id: v.call_id,
        media: v.media,
        event: v.event,
        duration: size(v.duration),
        key: opt(v.key, isStr),
      });
    default:
      // A kind of message this version does not know, perhaps from a newer app.
      return null;
  }
}

/**
 * Checks a message kept on this device. Messages saved by an older version of the app were never
 * checked, so they are checked again as they load.
 */
export function sanitizeMessage(m: ChatMessage): ChatMessage {
  let content = m.content;
  let problem = m.problem;
  if (content !== null) {
    content = parseContent(content);
    if (!content) problem = "malformed";
  }
  const reactions: Record<string, string[]> = {};
  if (isObj(m.reactions)) {
    for (const [key, users] of Object.entries(m.reactions)) {
      const ids = strings(users);
      if (ids?.length) reactions[key] = ids;
    }
  }
  const { editedFormat, ...rest } = m;
  return {
    ...rest,
    content,
    ...(problem ? { problem } : {}),
    edited: isStr(m.edited) ? m.edited : null,
    ...(editedFormat === "md" && isStr(m.edited) ? { editedFormat: "md" as const } : {}),
    reactions,
  };
}

/** Checks a list of stored messages. Anything that is not a message at all is left out. */
export function sanitizeMessages(list: unknown): ChatMessage[] {
  if (!Array.isArray(list)) return [];
  return list.filter((m): m is ChatMessage => isObj(m) && isStr(m.id) && isNum(m.seq)).map(sanitizeMessage);
}
